/**
 * useScheduleGenerator — wraps generateSchedule from the content package.
 *
 * Returns the GeneratorResult augmented with held preferences and a
 * regenerate callback. Calling with an undefined templateId returns a
 * passthrough result (no schedule, empty warnings) so callers can fall
 * back to a static schedule without conditional hook usage.
 *
 * UI-INV-005: pure synchronous computation, no side effects.
 */
import { useState, useMemo } from "react";
import {
  generateSchedule,
  getTemplate,
  type GeneratorResult,
  type UserRetreatPreferences,
} from "@retreats/content";
import type { EditableRetreatSchedule } from "@retreat/core";

// ── passthrough for the no-templateId case ─────────────────────────────────

function makePassthrough(
  staticSchedule?: EditableRetreatSchedule,
): GeneratorResult {
  return {
    schedule: staticSchedule ?? {
      id: "00000000-0000-0000-0000-000000000000",
      name: "",
      version: "2.0.0",
      days: [],
    },
    warnings: [],
  };
}

// ── hook ────────────────────────────────────────────────────────────────────

export type UseScheduleGeneratorResult = GeneratorResult & {
  preferences: UserRetreatPreferences;
  regenerate: (newPrefs: UserRetreatPreferences) => void;
};

export function useScheduleGenerator(
  templateId: string | undefined,
  startDateTime: Date,
  initialPreferences?: UserRetreatPreferences,
  /** Optional static schedule to embed when templateId is absent */
  fallbackSchedule?: EditableRetreatSchedule,
): UseScheduleGeneratorResult {
  const [preferences, setPreferences] = useState<UserRetreatPreferences>(
    initialPreferences ?? {},
  );

  const regenerate = (newPrefs: UserRetreatPreferences) =>
    setPreferences(newPrefs);

  const result = useMemo(() => {
    if (!templateId) return makePassthrough(fallbackSchedule);

    try {
      const template = getTemplate(templateId);
      return generateSchedule({ template, startDateTime, preferences });
    } catch {
      // Unknown templateId or generator error — passthrough so the caller
      // can still render with the static schedule.
      return makePassthrough(fallbackSchedule);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, startDateTime.getTime(), preferences, fallbackSchedule]);

  return { ...result, preferences, regenerate };
}
